package com.mrpowergamerbr.puppydough.webrune

import js.coroutines.promise
import js.buffer.ArrayBuffer
import js.date.Date
import js.iterable.asFlow
import js.objects.Record
import js.objects.recordOf
import js.promise.Promise
import js.typedarrays.Uint8Array
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import web.blob.arrayBuffer
import web.fs.*
import web.navigator.navigator
import web.storage.getDirectory
import web.streams.close

/**
 * The runner's save storage (`window.oprt.gameStorage`), backed by OPFS
 *
 * Files under [mount] (`/_savedata/file0`) are stored at `versions/<versionId>/save/file0`, the same folder used by the
 * import/export saves buttons. OPFS can't store the runner's timestamps or modes, so files report their last modified
 * time and a regular file mode. The timestamps never match the in-memory ones, so the runner rewrites the saves on
 * every sync, which is fine for a handful of small files.
 */
class OPFSGameStorage(private val versionId: String, private val mount: String) : GameStorage {
    companion object {
        const val MODE_FILE = 0x81B6 // 0o100666, regular file
        const val MODE_DIRECTORY = 0x41FF // 0o40777, directory
        private const val TYPE_MASK = 0xF000
    }

    // The runner may start multiple syncs at once, so OPFS access is serialized
    private val mutex = Mutex()

    override fun list(): Promise<Record<String, GameStorageListEntry>> = GlobalScope.promise {
        mutex.withLock {
            val entries = recordOf<String, GameStorageListEntry>()
            walk(saveDirectory(), "") { relativePath, handle ->
                entries["$mount/$relativePath"] = GameStorageListEntry(
                    timestamp = Date(if (handle is FileSystemFileHandle) handle.getFile().lastModified else 0.0)
                )
            }
            entries
        }
    }

    override fun get(path: String): Promise<GameStorageEntry> = GlobalScope.promise {
        mutex.withLock {
            val (parent, name) = parentOf(relativePath(path), create = false)

            val fileHandle = try {
                parent.getFileHandle(name, FileSystemGetFileOptions(create = false))
            } catch (e: Throwable) {
                null
            }

            if (fileHandle == null) {
                // Throws if it doesn't exist either, which fails the sync like any other missing entry
                parent.getDirectoryHandle(name, FileSystemGetDirectoryOptions(create = false))
                GameStorageEntry(timestamp = Date(0.0), mode = MODE_DIRECTORY, contents = null)
            } else {
                val file = fileHandle.getFile()
                GameStorageEntry(
                    timestamp = Date(file.lastModified),
                    mode = MODE_FILE,
                    contents = Uint8Array(file.arrayBuffer())
                )
            }
        }
    }

    override fun put(entry: GameStorageEntry, path: String): Promise<Unit> {
        // Copy before suspending, the contents may be a view of a MEMFS buffer that the game can write to in the meantime
        val contents = entry.contents?.slice()
        val isDirectory = entry.mode and TYPE_MASK == MODE_DIRECTORY and TYPE_MASK

        return GlobalScope.promise {
            mutex.withLock {
                val (parent, name) = parentOf(relativePath(path), create = true)

                if (isDirectory) {
                    parent.getDirectoryHandle(name, FileSystemGetDirectoryOptions(create = true))
                } else {
                    val writable = parent.getFileHandle(name, FileSystemGetFileOptions(create = true)).createWritable()
                    writable.write(contents ?: Uint8Array<ArrayBuffer>(0))
                    writable.close()
                }
            }
        }
    }

    override fun delete(path: String): Promise<Unit> = GlobalScope.promise {
        mutex.withLock {
            try {
                val (parent, name) = parentOf(relativePath(path), create = false)
                parent.removeEntry(name, FileSystemRemoveOptions(recursive = true))
            } catch (e: Throwable) {
                // Already gone
            }
        }
    }

    // "/_savedata/file0" -> "file0"
    private fun relativePath(path: String) = path.removePrefix(mount).trim('/')

    private suspend fun saveDirectory() = navigator.storage.getDirectory()
        .getDirectoryHandle("versions", FileSystemGetDirectoryOptions(create = true))
        .getDirectoryHandle(versionId, FileSystemGetDirectoryOptions(create = true))
        .getDirectoryHandle("save", FileSystemGetDirectoryOptions(create = true))

    private suspend fun parentOf(relativePath: String, create: Boolean): Pair<FileSystemDirectoryHandle, String> {
        val elements = relativePath.split("/")
        var handle = saveDirectory()
        for (element in elements.dropLast(1))
            handle = handle.getDirectoryHandle(element, FileSystemGetDirectoryOptions(create = create))
        return handle to elements.last()
    }

    private suspend fun walk(
        directory: FileSystemDirectoryHandle,
        prefix: String,
        block: suspend (String, FileSystemHandle) -> Unit
    ) {
        directory.values().asFlow().collect { handle ->
            val relativePath = prefix + handle.name
            block(relativePath, handle)
            if (handle is FileSystemDirectoryHandle)
                walk(handle, "$relativePath/", block)
        }
    }
}
